import React from 'react';
import { Table, Input, InputNumber, Button, DatePicker } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FG164 } from '../../../services/pwg208Api';

interface Tab4InvoiceItemsProps {
  invoiceItems: FG164[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onUpdate: <K extends keyof FG164>(idx: number, key: K, value: FG164[K]) => void;
}

const Tab4InvoiceItems: React.FC<Tab4InvoiceItemsProps> = React.memo(({
  invoiceItems,
  isEditing,
  onAdd,
  onDelete,
  onUpdate,
}) => {
  const toDateVal = (v: string | null) => (v ? dayjs(v) : null);
  const fromDateVal = (val: dayjs.Dayjs | null): string | null =>
    val ? val.format('YYYY-MM-DD') + 'T00:00:00Z' : null;

  const columns: ColumnsType<FG164> = [
    {
      title: <span data-contract-field="fg164_02">序號</span>,
      dataIndex: 'fg164_02',
      key: 'fg164_02',
      width: 60,
      align: 'center',
    },
    {
      title: <span data-contract-field="fg164_10">發票號碼</span>,
      dataIndex: 'fg164_10',
      key: 'fg164_10',
      width: 160,
      render: (v: string, _record: FG164, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg164_10', e.target.value)}
            maxLength={20}
          />
        ) : v,
    },
    {
      title: <span data-contract-field="fg164_11">發票日期</span>,
      dataIndex: 'fg164_11',
      key: 'fg164_11',
      width: 140,
      render: (v: string | null, _record: FG164, idx: number) =>
        isEditing ? (
          <DatePicker
            size="small"
            value={toDateVal(v)}
            onChange={(val) => onUpdate(idx, 'fg164_11', fromDateVal(val))}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
          />
        ) : (
          v ? dayjs(v).format('YYYY-MM-DD') : ''
        ),
    },
    {
      title: <span data-contract-field="fg164_12">發票金額(CJ含稅)</span>,
      dataIndex: 'fg164_12',
      key: 'fg164_12',
      width: 140,
      align: 'right',
      render: (v: number, _record: FG164, idx: number) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            precision={2}
            onChange={(val) => onUpdate(idx, 'fg164_12', val ?? 0)}
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString()
        ),
    },
    {
      title: <span data-contract-field="fg164_13">發票金額(CF未稅)</span>,
      dataIndex: 'fg164_13',
      key: 'fg164_13',
      width: 140,
      align: 'right',
      render: (v: number, _record: FG164, idx: number) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            precision={2}
            onChange={(val) => onUpdate(idx, 'fg164_13', val ?? 0)}
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString()
        ),
    },
    {
      title: <span data-contract-field="fg164_91">修改人</span>,
      dataIndex: 'fg164_91',
      key: 'fg164_91',
      width: 100,
      render: (v: string) => v || '',
    },
    ...(isEditing
      ? [
          {
            title: '',
            key: 'actions',
            width: 40,
            render: (_: unknown, _record: FG164, idx: number) => (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(idx)}
              />
            ),
          },
        ]
      : []),
  ];

  const totalCJ = invoiceItems.reduce((s, r) => s + (r.fg164_12 || 0), 0);
  const totalCF = invoiceItems.reduce((s, r) => s + (r.fg164_13 || 0), 0);

  return (
    <div className="pwg208-tab4">
      {isEditing && (
        <div className="tab-toolbar">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={onAdd}
            type="dashed"
          >
            新增發票
          </Button>
        </div>
      )}
      <Table<FG164>
        columns={columns}
        dataSource={invoiceItems}
        rowKey={(r) => `${r.fg164_01}-${r.fg164_02}`}
        pagination={false}
        size="small"
        scroll={{ y: 300 }}
        locale={{ emptyText: '無發票資料' }}
      />
      <div className="tab-summary">
        共 <strong>{invoiceItems.length}</strong> 筆 |
        CJ合計: <strong>{totalCJ.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong> |
        CF合計: <strong>{totalCF.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong>
      </div>
    </div>
  );
});

Tab4InvoiceItems.displayName = 'Tab4InvoiceItems';

export default Tab4InvoiceItems;
